import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare, faTrashCan } from '@fortawesome/pro-light-svg-icons';
import UpdateNoteForm from '../../../../Hubs/HubDetails/tabs/components/UpdateNoteForm';
import DeleteNoteConfirmation from '../../../../Hubs/HubDetails/tabs/components/DeleteNoteConfirmation';
import NoteActionFeedbackModel from '../../../../Hubs/HubDetails/tabs/components/NoteActionFeedbackModel';
import { parseDateToLocal } from '../../../../../shared/utils';
import { MedicationListItem } from '../../../../../store/hubOwner/types';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchMedicationNotes,
  addMedicationNote,
  updateMedicationNote,
  deleteMedicationNote,
  clearMedicationNoteActionStatus,
} from '../../../../../store/medications/actionCreators';
import { RootState } from '../../../../../store';
import { dimGrey, lightBlack, lightGrey } from '../../../../../mainTheme';

interface MedicationNotesProps {
  rowData: MedicationListItem | null;
  patientId: number;
}

const StyledTextField = styled(TextField)({
  width: '100%',
  marginBottom: '16px',
});

const DetailLabel = styled(Typography)({
  color: lightBlack,
  fontWeight: 700,
  fontSize: '16px',
  lineHeight: '24px',
  letterSpacing: '0.2px',
});

const DetailValue = styled(Typography)({
  color: dimGrey,
  fontWeight: 500,
  fontSize: '16px',
  lineHeight: '24px',
  letterSpacing: '0.2px',
});

export const MedicationNotes: React.FC<MedicationNotesProps> = ({
  rowData,
  patientId,
}) => {
  const dispatch = useDispatch();
  const [newNote, setNewNote] = useState('');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedNote, setSelectedNote] = useState<string>('');
  const [selectedNoteId, setSelectedNoteId] = useState<number | null>(null);

  const { medicationNotes, isNotesLoading, noteActionStatus } = useSelector(
    (state: RootState) => state.medications,
  );

  const ActionCell = ({
    params,
  }: {
    params: {
      id: number;
      noteData: string;
    };
  }) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        border: '1px solid var(--Outline-O5---Separator, #D4D4D9)',
        borderRadius: '8px',
        padding: { xs: '2px', sm: '4px' },
        height: { xs: '32px', sm: '36px' },
      }}
    >
      <Tooltip title="Edit">
        <IconButton
          onClick={() => handleUpdateConfirm(params.id, params.noteData)}
        >
          <FontAwesomeIcon
            icon={faPenToSquare}
            style={{
              fontSize: '16px',
              color: dimGrey,
            }}
          />
        </IconButton>
      </Tooltip>
      <Box
        sx={{
          width: '1px',
          height: '100%',
          bgcolor: 'var(--Outline-O5---Separator, #D4D4D9)',
        }}
      />
      <Tooltip title="Delete">
        <IconButton onClick={() => handleDeleteConfirm(params.id)}>
          <FontAwesomeIcon
            icon={faTrashCan}
            style={{
              fontSize: '16px',
              color: dimGrey,
            }}
          />
        </IconButton>
      </Tooltip>
    </Box>
  );

  // Extract ndc from rowData
  const ndc = rowData?.national_drug_code;

  useEffect(() => {
    if (patientId && ndc) {
      dispatch(fetchMedicationNotes(patientId, ndc));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, ndc]);

  const handleSaveNote = () => {
    if (newNote.trim() && patientId && ndc) {
      dispatch(addMedicationNote(patientId, ndc, newNote.trim()));
      setNewNote('');
    }
  };

  const handleUpdateConfirm = (id: number, noteData: string) => {
    setSelectedNoteId(id);
    setSelectedNote(noteData);
    setEditModalOpen(true);
  };

  const handleDeleteConfirm = (noteId: number) => {
    setSelectedNoteId(noteId);
    setDeleteConfirmOpen(true);
  };

  const handleUpdate = (values: { note: string; category: string }) => {
    if (selectedNoteId && patientId && ndc) {
      dispatch(
        updateMedicationNote(selectedNoteId, values.note, patientId, ndc),
      );
      setEditModalOpen(false);
      setSelectedNoteId(null);
    }
  };

  const handleDelete = () => {
    if (selectedNoteId && patientId && ndc) {
      dispatch(deleteMedicationNote(selectedNoteId, patientId, ndc));
      setDeleteConfirmOpen(false);
      setSelectedNoteId(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setSelectedNoteId(null);
  };

  return isNotesLoading ? (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight={200}
    >
      <CircularProgress />
    </Box>
  ) : (
    <Box sx={{ py: 2 }}>
      <Box>
        <StyledTextField
          placeholder="Type here to add a new note..."
          multiline
          rows={2}
          variant="standard"
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          sx={{
            '& .MuiInputBase-root': {
              borderRadius: '8px',
              border: '1px solid #D4D4D9',
              '& textarea': {
                '&::placeholder': {
                  color: lightGrey,
                },
              },
              '&::before': {
                display: 'none',
              },
              '&::after': {
                display: 'none',
              },
            },
            '& .MuiInputBase-input': {
              padding: '12px',
              color: lightBlack,
            },
          }}
        />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginBottom: '16px',
          }}
        >
          <Button
            variant="contained"
            color="secondary"
            onClick={handleSaveNote}
            disabled={!newNote.trim()}
          >
            Save note
          </Button>
        </Box>
      </Box>
      <Box
        sx={{
          maxHeight: '290px',
          overflowY: 'auto',
        }}
      >
        {medicationNotes?.map((note) => (
          <Box
            key={note.note_id}
            sx={{
              mb: 2,
              p: 2,
              border: '1px solid #D4D4D9',
              borderRadius: '8px',
            }}
          >
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}
            >
              <Box>
                <DetailLabel>Added by</DetailLabel>
                <DetailValue>{note.created_by}</DetailValue>
              </Box>
              <Box>
                <DetailLabel>Date added</DetailLabel>
                <DetailValue>
                  {note.created_at
                    ? parseDateToLocal(note.created_at, 'yyyy-MM-dd HH:mm:ss')
                    : 'N/A'}
                </DetailValue>
              </Box>
              <ActionCell
                params={{
                  id: note.note_id || 0,
                  noteData: note.body || '',
                }}
              />
            </Box>
            <DetailLabel>Note</DetailLabel>
            <DetailValue>{note.body || ''}</DetailValue>
          </Box>
        ))}
      </Box>
      {editModalOpen && (
        <UpdateNoteForm
          initialValues={{
            note: selectedNote,
            category: 'medication',
          }}
          showCategory={false}
          noteTypeOptions={[]}
          onSubmit={handleUpdate}
          onClose={() => {
            setEditModalOpen(false);
            setSelectedNoteId(null);
          }}
          backdropBlur
        />
      )}

      {/* Delete Confirmation Dialog */}
      {deleteConfirmOpen && (
        <DeleteNoteConfirmation
          onClose={handleCancelDelete}
          onDelete={handleDelete}
          backdropBlur
        />
      )}

      {/* API response based Popup model */}
      {noteActionStatus.show && (
        <NoteActionFeedbackModel
          onClose={() => dispatch(clearMedicationNoteActionStatus())}
          medicationNoteFeedback
        />
      )}
    </Box>
  );
};
